import { request } from "@prismicio/editor-support/Http"
import { captureException } from "@sentry/react"
import { unknown } from "zod"

import { getRepositoryName } from "./routing"
import { baseUrl } from "./services/core"

async function trackEvent(name: string, traits: Record<string, string>) {
  const body = {
    name,
    source: "Page Builder",
    traits,
    groupId: getRepositoryName()
  }

  return request(new URL("tracking/trace", baseUrl), {
    method: "POST",
    body,
    keepalive: true,
    okType: unknown()
  }).catch(captureException)
}

type SliceProps = {
  id: string
  variation: string
  zone: string
  customtypeId: string
  sliceName: string
  documentName: string
  type: "shared" | "composite" | "simple"
}

type SliceAddedProps = SliceProps & {
  promptId?: string
}

export function trackAddSlice(params: SliceAddedProps) {
  void trackEvent("Slice Added", params)
}

export function trackDeleteSlice(params: SliceProps) {
  void trackEvent("Slice Deleted", params)
}

export function trackDuplicateSlice(params: SliceProps) {
  void trackEvent("Slice Duplicated", params)
}

type SliceVariationChangedProps = SliceProps & {
  variationName: string
}

export function trackSliceVariationChanged(params: SliceVariationChangedProps) {
  void trackEvent("Slice Variation Changed", params)
}

export function trackSimulatorUrlSetup(params: { url: string }) {
  void trackEvent("Simulator URL Setup", params)
}

export function trackPreviewOpen(params: {
  documentId: string
  label: string
}) {
  void trackEvent("Open Preview", params)
}

// N.B. this event is fired just before navigating away from the page, so needs to be awaited
// see fetch keepalive support: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API#browser_compatibility
export async function trackSwitchToOldEditor(params: {
  documentId?: string
  customTypeId: string
}) {
  return trackEvent("Switch to Legacy Editor", params)
}

export function trackSlicePromptedStarted(params: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
}) {
  void trackEvent("Slice Prompted Started", params)
}

export function trackSlicePromptedDone(params: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
  slices: string
  cost: string
  time: string
}) {
  void trackEvent("Slice Prompted Done", params)
}

export function trackRefineSliceStarted(params: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
}) {
  void trackEvent("Refine Slice Started", params)
}

export function trackRefineSliceDone(params: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
  cost: string
  time: string
}) {
  void trackEvent("Refine Slice Done", params)
}

export function trackSmartCopyPasteStarted(params: {
  customTypeId: string
  documentId?: string
  sliceId: string
  variationId: string
  retried?: "true"
}) {
  void trackEvent("Smart Copy Paste Started", params)
}

export function trackSmartCopyPasteDone(params: {
  customTypeId: string
  documentId?: string
  sliceId: string
  variationId: string
  retried?: "true"
  cost: string
  time: string
  distance: string
}) {
  void trackEvent("Smart Copy Paste Done", params)
}

export function trackLivePreviewModalOpen(params: {
  documentId: string
  customTypeId: string
  repositoryId: string
}) {
  void trackEvent("Live Preview Modal Open", params)
}

export function trackWhatsNewModalOpen(params: {
  documentId: string
  customTypeId: string
  repositoryId: string
}) {
  void trackEvent("Whats New Modal Open", params)
}

export function trackEditorEvent(
  event: string,
  params: Record<string, string> = {}
) {
  void trackEvent(event, params)
}

export function trackDocumentWithLiveEditingPreview(params: {
  documentId: string
  customTypeId: string
  repositoryId: string
  preview: "true" | "false"
  error?: string
}) {
  void trackEvent("Document With Live Editing Preview", params)
}
